/**
 * 双重预防机制 - 风险排查管理 API
 * 包括：排查任务执行、排查任务整改、整改监管、排查任务查询
 */
import request from "@/utils/enhancedRequest";

// ==================== 排查任务执行 ====================

/**
 * 获取排查任务列表（分页）
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getInspectionTaskPage(data) {
  return request({
    url: `/sa/green/pre/risk-point-tasks/page`,
    method: "post",
    data,
  });
}

/**
 * 获取排查任务详情
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function getInspectionTaskDetail(id) {
  return request({
    url: `/sa/green/pre/risk-point-tasks/get?id=${id}`,
    method: "get",
  });
}

/**
 * 执行排查任务
 * @param {Object} data - 执行数据
 * @returns {Promise}
 */
export function executeInspectionTask(data) {
  return request({
    url: `/sa/safety/inspectionTask/execute`,
    method: "post",
    data,
  });
}

/**
 * 完成排查任务
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function completeInspectionTask(id) {
  return request({
    url: `/sa/safety/inspectionTask/complete/${id}`,
    method: "post",
  });
}

// ==================== 排查任务整改 ====================

/**
 * 获取整改任务列表（分页）
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getRectificationTaskPage(data) {
  return request({
    url: `/sa/green/pre/risk-point-tasks-rectification/page`,
    method: "post",
    data,
  });
}

/**
 * 获取整改详情
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function getRectificationDetail(id) {
  return request({
    url: `/sa/green/pre/risk-point-tasks-rectification/get?id=${id}`,
    method: "get",
  });
}

/**
 * 执行整改
 * @param {Object} data - 整改数据
 * @returns {Promise}
 */
export function executeRectification(data) {
  return request({
    url: `/sa/green/pre/risk-point-tasks-rectification/rectification`,
    method: "put",
    data,
  });
}

// ==================== 整改监管 ====================

/**
 * 获取整改监管列表（分页）
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getRectificationSupervisionPage(data) {
  return request({
    url: `/sa/green/pre/risk-point-tasks-rectification/page`,
    method: "post",
    data,
  });
}

/**
 * 整改确认
 * @param {Object} data - 确认数据
 * @returns {Promise}
 */
export function confirmRectification(data) {
  return request({
    url: `/sa/green/pre/risk-point-tasks-rectification/confirm`,
    method: "put",
    data,
  });
}

/**
 * 获取整改详情（用于监管）
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function getRectificationSupervisionDetail(id) {
  return request({
    url: `/sa/green/pre/risk-point-tasks-rectification/rectification?id=${id}`,
    method: "get",
  });
}

// ==================== 排查任务查询 ====================

/**
 * 获取排查任务查询列表（分页）
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getInspectionTaskQueryPage(data) {
  return request({
    url: `/sa/safety/inspectionTask/queryPage`,
    method: "post",
    data,
  });
}

/**
 * 获取排查统计信息
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getInspectionStatistics(data) {
  return request({
    url: `/sa/safety/inspectionTask/statistics`,
    method: "post",
    data,
  });
}

/**
 * 获取排查执行统计信息
 * @param {Object} data - 查询参数
 * @param {String} data.projectId - 当前用户项目id
 * @param {String} data.startDate - 开始日期
 * @param {String} data.endDate - 结束日期
 * @returns {Promise}
 */
export function getInspectionExecutionStatistics(data) {
  return request({
    url: `/sa/green/pre/risk-point-tasks/statistics/execution`,
    method: "post",
    data,
  });
}

/**
 * 导出排查任务
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function exportInspectionTask(data) {
  return request({
    url: `/sa/green/pre/risk-point-tasks/export`,
    method: "post",
    data,
  });
}

// ==================== 自发任务记录 ====================

/**
 * 创建自发任务
 * @param {Object} data - 任务数据
 * @returns {Promise}
 */
export function createSpontaneousTask(data) {
  return request({
    url: `/sa/green/pre/risk-point-spontaneous-tasks/create`,
    method: "post",
    data,
  });
}

/**
 * 更新自发任务
 * @param {Object} data - 任务数据
 * @returns {Promise}
 */
export function updateSpontaneousTask(data) {
  return request({
    url: `/sa/green/pre/risk-point-spontaneous-tasks/update`,
    method: "put",
    data,
  });
}

/**
 * 获取自发任务列表（分页）
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function getSpontaneousTaskPage(data) {
  return request({
    url: `/sa/green/pre/risk-point-spontaneous-tasks/page`,
    method: "post",
    data,
  });
}

/**
 * 获取自发任务详情
 * @param {String} id - 任务ID
 * @returns {Promise}
 */
export function getSpontaneousTaskDetail(id) {
  return request({
    url: `/sa/green/pre/risk-point-spontaneous-tasks/get?id=${id}`,
    method: "get",
  });
}

/**
 * 导出自发任务
 * @param {Object} data - 查询参数
 * @returns {Promise}
 */
export function exportSpontaneousTask(data) {
  return request({
    url: `/sa/green/pre/risk-point-spontaneous-tasks/export`,
    method: "post",
    data,
  });
}
